import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ChatIcon from '@mui/icons-material/Chat';
import CloseIcon from '@mui/icons-material/Close';
import { Badge, Box, Fab, Grow, useTheme, Zoom } from '@mui/material';

import { FRONT_CHAT_CONFIG } from '../config';
import {
  selectFrontChatLauncherPosition,
  selectFrontChatLauncherVisible,
  selectFrontChatUnreadCount,
  selectFrontChatWindowOpen,
  toggleChatWindow,
} from '../store';

export const ChatLauncher = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const visible = useSelector(selectFrontChatLauncherVisible);
  const open = useSelector(selectFrontChatWindowOpen);
  const unread = useSelector(selectFrontChatUnreadCount);
  const position = useSelector(selectFrontChatLauncherPosition);

  const handleClick = useCallback(() => {
    dispatch(toggleChatWindow());
  }, [dispatch]);

  return (
    <>
      <Zoom
        in={visible}
        timeout={theme.transitions.duration.enteringScreen}
        style={{
          transitionDelay: visible ? `${String(theme.transitions.duration.shortest)}ms` : '0ms',
        }}
      >
        <Box
          sx={{
            position: 'fixed',
            bottom: theme.spacing(position.bottom),
            ...(position.side === 'left'
              ? { left: theme.spacing(FRONT_CHAT_CONFIG.launcher.position.x) }
              : { right: theme.spacing(FRONT_CHAT_CONFIG.launcher.position.x) }),
            zIndex: 1200, // Below Dialog (1300) but above most content
          }}
        >
          <Badge
            badgeContent={unread}
            color="secondary"
            invisible={unread === 0 || open}
            overlap="circular"
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            aria-live="polite"
            aria-label={unread > 0 ? `${String(unread)} unread messages` : undefined}
            sx={{
              '& .MuiBadge-badge': {
                zIndex: 1201, // Just above the FAB
              },
            }}
          >
            <Fab
              onClick={handleClick}
              size="medium"
              color="primary"
              aria-label={open ? 'Close chat window' : 'Open chat window'}
              aria-expanded={open}
              aria-haspopup="dialog"
              role="button"
              tabIndex={0}
              sx={{
                transition: theme.transitions.create(
                  ['transform', 'background-color', 'box-shadow'],
                  {
                    duration:
                      theme.transitions.duration[FRONT_CHAT_CONFIG.launcher.animation.duration],
                  },
                ),
                '&:hover': {
                  transform: `scale(${String(FRONT_CHAT_CONFIG.launcher.animation.hoverScale)})`,
                  boxShadow: theme.shadows[8],
                },
                // Subtle bounce animation on mount
                animation: visible ? 'bounce-in 0.5s ease-out' : 'none',
                '@keyframes bounce-in': {
                  '0%': {
                    transform: 'scale(0)',
                  },
                  '50%': {
                    transform: 'scale(1.1)',
                  },
                  '100%': {
                    transform: 'scale(1)',
                  },
                },
              }}
            >
              <Grow in={true} timeout={300}>
                {open ? <CloseIcon /> : <ChatIcon />}
              </Grow>
            </Fab>
          </Badge>
        </Box>
      </Zoom>
      {/* Screen reader announcements */}
      <Box
        component="div"
        role="status"
        aria-live="polite"
        aria-atomic="true"
        sx={{
          position: 'absolute',
          left: '-10000px',
          width: '1px',
          height: '1px',
          overflow: 'hidden',
        }}
      >
        {unread > 0 && `You have ${String(unread)} unread chat message${unread === 1 ? '' : 's'}`}
      </Box>
    </>
  );
};
